// @flow
import invariant from 'invariant';
import queryString from 'query-string';
import { sprintf } from 'sprintf-js';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { Location, RouterHistory } from 'react-router';
import { createCookie, deleteCookie } from 'utils/cookies';
import configureTrackJs from 'utils/configureTrackJs';
import { logIn } from 'api/currentUser';
import { trackLogin } from 'utils/analytics';
import LoginForm from 'components/LoginForm';
import getThirdPartyLoginUrl from 'utils/getThirdPartyLoginUrl';
import { updateCurrentUser } from '../redux/main/actions/currentUser';
import { pushFlashMessage } from '../redux/main/actions/flashMessages';
import { showModal } from '../redux/main/actions/modal';

type Props = {
  isModal: boolean,
  redirectTo?: string,
  email?: string,
  savedListing?: {
    id: string,
    type: string,
    name: string
  },
  subscribedSubsite?: string,
  inviteId?: ?string,
  onLogin: ?Function,
  signUpOverrideFn?: Function,
  location: Location,
  history: RouterHistory,
  updateCurrentUser: typeof updateCurrentUser,
  pushFlashMessage: typeof pushFlashMessage,
  showModal: typeof showModal,
  header?: React.Node,
  centered?: boolean
};

type State = {
  thirdPartyLoginError: ?string
};

class LoginFormContainer extends React.Component<Props, State> {
  state = {
    thirdPartyLoginError: null
  };

  isMounted = false;

  get initialValues() {
    return {
      email: this.props.email || '',
      password: ''
    };
  }

  componentDidMount() {
    this.isMounted = true;
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  static getDerivedStateFromProps(nextProps) {
    const { error, provider } = queryString.parse(nextProps.location.search);

    if (!error) return null;

    let errorMessage;

    if (error === 'EXISTING_ACCOUNT') {
      invariant(provider, 'missing provider');
      // TRANSLATORS: error message when user tries to log in using
      // TRANSLATORS: Facebook or Google but they already had an account
      // TRANSLATORS: that did not use that login provider
      errorMessage = sprintf(
        getText(
          'Please log in first to associate that %(provider)s ' +
            'account to your existing Idealist account'
        ),
        // capitalize the first letter of the provider name
        { provider: provider[0].toUpperCase() + provider.substring(1) }
      );
    }

    if (error === 'MISMATCHED_ACCOUNTS') {
      invariant(provider, 'missing provider');
      // TRANSLATORS: User tried to link Facebook/Google account, but
      // TRANSLATORS: it was already used by another Idealist acccount
      errorMessage = sprintf(
        getText(
          'That %(provider)s account is already associated with ' +
            'another Idealist account'
        ),
        // capitalize the first letter of the provider name
        { provider: provider[0].toUpperCase() + provider.substring(1) }
      );
    }

    if (error === 'FAILED_OAUTH_LOGIN') {
      // TRANSLATORS: user used 3rd party authentication like Google, but it failed
      errorMessage = getText(
        'Authentication did not work as expected, please try again.'
      );
    }

    if (error === 'USER_FROZEN') {
      // TRANSLATORS: user tried to login, but user is frozen
      errorMessage = getText(
        'Your account has been frozen for violating our Terms of Service.'
      );
    }

    if (errorMessage) {
      // remove error and provider params from URL
      const params = queryString.parse(nextProps.location.search);
      delete params.error;
      delete params.provider;
      nextProps.history.replace({
        pathname: nextProps.location.pathname,
        search: queryString.stringify(params)
      });
      return { thirdPartyLoginError: errorMessage };
    }
    return null;
  }

  showSignupModal = (email: string) => {
    this.props.showModal('SIGNUP', {
      redirectTo: this.props.redirectTo,
      savedListing: this.props.savedListing,
      subscribedSubsite: this.props.subscribedSubsite,
      email
    });
  };

  getSignupUrl = (email: string) => {
    const {
      redirectTo,
      inviteId,
      savedListing,
      subscribedSubsite
    } = this.props;
    return {
      pathname: '/signup',
      state: {
        redirectTo,
        email,
        inviteId,
        savedListing,
        subscribedSubsite
      }
    };
  };

  showForgotPasswordModal = (state: {
    email: string,
    showUnsupportedServiceMessage?: boolean
  }) => {
    this.props.showModal('FORGOT_PASSWORD', state);
  };

  getForgotPasswordUrl = (state: {
    email: string,
    showUnsupportedServiceMessage?: boolean
  }) => ({
    pathname: '/forgot-password',
    state
  });

  getThirdPartyLoginUrl = (provider: 'facebook' | 'google') => {
    const {
      redirectTo,
      inviteId,
      savedListing,
      subscribedSubsite
    } = this.props;
    return getThirdPartyLoginUrl({
      provider,
      redirectTo,
      inviteId,
      savedListing,
      subscribedSubsite
    });
  };

  submitLogin = ({
    values,
    handleError
  }: {
    values: Object,
    handleError: Function
  }) => {
    deleteCookie('userDismissedNotificationPreferencesDialog');

    const loginInfo = {};
    Object.assign(loginInfo, values);
    if (this.props.savedListing) {
      loginInfo.listing = this.props.savedListing;
    }
    if (this.props.subscribedSubsite) {
      loginInfo.subscribeSubsiteId = this.props.subscribedSubsite;
    }
    logIn(loginInfo)
      .then(user => {
        if (user.id) {
          trackLogin(user, 'Email Password');
          configureTrackJs(user.id);
        }

        if (user.locale) {
          createCookie('_LOCALE_', user.locale, 1825);
        }

        this.props.updateCurrentUser(user);
        if (this.props.savedListing) {
          const { type, name } = this.props.savedListing;
          if (type === 'ORG') {
            this.props.pushFlashMessage({
              type: 'SAVE_ORG',
              data: { orgName: name }
            });
          } else if (type === 'GROUP') {
            this.props.pushFlashMessage({
              type: 'SAVE_GROUP',
              data: { groupName: name }
            });
          }
        }

        if (this.props.subscribedSubsite) {
          this.props.pushFlashMessage({ type: 'SUBSCRIBED_SUBSITE' });
        }

        if (this.props.onLogin) {
          this.props.onLogin(user);
        }
      })
      .catch(handleError)
      .finally(() => {
        if (this.isMounted) {
          this.setState({ thirdPartyLoginError: null });
        }
      });
  };

  render() {
    return (
      <LoginForm
        initialValues={this.initialValues}
        thirdPartyLoginError={this.state.thirdPartyLoginError}
        isModal={this.props.isModal}
        showSignupModal={this.showSignupModal}
        getSignupUrl={this.getSignupUrl}
        signUpOverrideFn={this.props.signUpOverrideFn}
        showForgotPasswordModal={this.showForgotPasswordModal}
        getForgotPasswordUrl={this.getForgotPasswordUrl}
        getThirdPartyLoginUrl={this.getThirdPartyLoginUrl}
        submitLogin={this.submitLogin}
        header={this.props.header}
        centered={this.props.centered}
      />
    );
  }
}

export default withRouter(
  connect(
    null,
    { updateCurrentUser, pushFlashMessage, showModal }
  )(LoginFormContainer)
);
